import React, { useState } from 'react';
import { PencilIcon, CheckIcon, XMarkIcon, CreditCardIcon, UsersIcon, DocumentTextIcon, CloudIcon, CalendarDaysIcon, InformationCircleIcon, ExclamationTriangleIcon, CodeBracketIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import { Heading } from '../components/heading';
import { Text } from '../components/text';
import { Input } from '../components/input';
import { Button } from '../components/button';
import { Listbox, ListboxOption, ListboxLabel } from '../components/listbox';
import { Table, TableHeader, TableHead, TableRow, TableCell, TableBody } from '../components/table';
import { doc, updateDoc } from "firebase/firestore";
import { ref as storageRef, getDownloadURL } from 'firebase/storage';
import { db, storage } from "../firebase";
import { createCheckoutSession, createPortalSession } from '../api/billing';
import SubscriptionStatus from '../components/SubscriptionStatus';

// Pago amount semantics: new pagos written via build_payment_doc store
// baseAmount (subtotal pre-IVA), ivaAmount, and amount (total incl. IVA).
// Legacy pagos (pre-build_payment_doc) only have `amount` and the CRM
// historically treated it as subtotal — preserve that fallback so old rows
// keep displaying the same way they did before.
const derivePagoAmounts = (pago) => {
  if (pago?.baseAmount != null && pago?.amount != null) {
    const subtotal = Number(pago.baseAmount);
    const iva = pago.ivaAmount != null ? Number(pago.ivaAmount) : Number(pago.amount) - subtotal;
    const total = Number(pago.amount);
    return { subtotal, iva, total };
  }
  const legacySubtotal = Number(pago?.amount || 0);
  return { subtotal: legacySubtotal, iva: legacySubtotal * 0.16, total: legacySubtotal * 1.16 };
};

const openFacturaFromStorage = async (path) => {
  try {
    const url = await getDownloadURL(storageRef(storage, path));
    window.open(url, '_blank', 'noopener,noreferrer');
  } catch (err) {
    // eslint-disable-next-line no-alert
    alert('No se pudo descargar la factura: ' + (err?.message || 'error desconocido'));
  }
};

// Force-download via a temporary blob URL. Needed for XML because browsers
// render XML inline in a tab by default — the user wants the file, not a viewer.
const downloadFacturaFromStorage = async (path, suggestedFilename) => {
  try {
    const url = await getDownloadURL(storageRef(storage, path));
    const response = await fetch(url);
    if (!response.ok) throw new Error('HTTP ' + response.status);
    const blob = await response.blob();
    const objectUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objectUrl;
    a.download = suggestedFilename || 'factura';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(objectUrl);
  } catch (err) {
    // eslint-disable-next-line no-alert
    alert('No se pudo descargar la factura: ' + (err?.message || 'error desconocido'));
  }
};

// SAT Uso CFDI catalog
const USO_CFDI_OPTIONS = [
  { value: 'G01', label: 'G01 - Adquisición de mercancías' },
  { value: 'G02', label: 'G02 - Devoluciones, descuentos o bonificaciones' },
  { value: 'G03', label: 'G03 - Gastos en general' },
  { value: 'I01', label: 'I01 - Construcciones' },
  { value: 'I02', label: 'I02 - Mobiliario y equipo de oficina' },
  { value: 'I03', label: 'I03 - Equipo de transporte' },
  { value: 'I04', label: 'I04 - Equipo de cómputo y accesorios' },
  { value: 'I05', label: 'I05 - Dados, troqueles, moldes, matrices y herramental' },
  { value: 'I06', label: 'I06 - Comunicaciones telefónicas' },
  { value: 'I07', label: 'I07 - Comunicaciones satelitales' },
  { value: 'I08', label: 'I08 - Otra maquinaria y equipo' },
  { value: 'D01', label: 'D01 - Honorarios médicos, dentales y gastos hospitalarios' },
  { value: 'D02', label: 'D02 - Gastos médicos por incapacidad o discapacidad' },
  { value: 'D03', label: 'D03 - Gastos funerales' },
  { value: 'D04', label: 'D04 - Donativos' },
  { value: 'D05', label: 'D05 - Intereses reales por créditos hipotecarios' },
  { value: 'D06', label: 'D06 - Aportaciones voluntarias al SAR' },
  { value: 'D07', label: 'D07 - Primas por seguros de gastos médicos' },
  { value: 'D08', label: 'D08 - Gastos de transportación escolar obligatoria' },
  { value: 'D09', label: 'D09 - Depósitos en cuentas para el ahorro' },
  { value: 'D10', label: 'D10 - Pagos por servicios educativos (colegiaturas)' },
  { value: 'S01', label: 'S01 - Sin efectos fiscales' },
  { value: 'CP01', label: 'CP01 - Pagos' },
  { value: 'CN01', label: 'CN01 - Nómina' },
];

// SAT Regimen Fiscal catalog
const REGIMEN_FISCAL_OPTIONS = [
  { value: '601', label: '601 - General de Ley Personas Morales' },
  { value: '603', label: '603 - Personas Morales con Fines no Lucrativos' },
  { value: '605', label: '605 - Sueldos y Salarios e Ingresos Asimilados' },
  { value: '606', label: '606 - Arrendamiento' },
  { value: '607', label: '607 - Régimen de Enajenación o Adquisición de Bienes' },
  { value: '608', label: '608 - Demás ingresos' },
  { value: '610', label: '610 - Residentes en el Extranjero sin Establecimiento Permanente' },
  { value: '611', label: '611 - Ingresos por Dividendos (socios y accionistas)' },
  { value: '612', label: '612 - Personas Físicas con Actividades Empresariales y Profesionales' },
  { value: '614', label: '614 - Ingresos por intereses' },
  { value: '615', label: '615 - Régimen de los ingresos por obtención de premios' },
  { value: '616', label: '616 - Sin obligaciones fiscales' },
  { value: '620', label: '620 - Sociedades Cooperativas de Producción' },
  { value: '621', label: '621 - Incorporación Fiscal' },
  { value: '622', label: '622 - Actividades Agrícolas, Ganaderas, Silvícolas y Pesqueras' },
  { value: '623', label: '623 - Opcional para Grupos de Sociedades' },
  { value: '624', label: '624 - Coordinados' },
  { value: '625', label: '625 - Actividades Empresariales con Plataformas Tecnológicas' },
  { value: '626', label: '626 - Régimen Simplificado de Confianza' },
];

// Helper to format currency
const formatCurrency = (amount) => {
  return new Intl.NumberFormat('es-MX', {
    style: 'currency',
    currency: 'MXN',
  }).format(amount);
};

// Helper to format date
const formatDate = (timestamp) => {
  if (!timestamp) return '-';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  return new Intl.DateTimeFormat('es-MX', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date);
};

// Helper to calculate valid until date
const calculateValidUntil = (timestamp, monthsValidity) => {
  if (!timestamp || !monthsValidity) return null;
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  const validUntil = new Date(date);
  validUntil.setMonth(validUntil.getMonth() + monthsValidity);
  validUntil.setDate(validUntil.getDate() - 1);
  return validUntil;
};

// Check if a payment is still valid (not expired)
const isPaymentValid = (timestamp, monthsValidity) => {
  const validUntil = calculateValidUntil(timestamp, monthsValidity);
  if (!validUntil) return false;
  return validUntil > new Date();
};

const ConfiguracionFacturacion = ({ pagos, pagosLoading, pagosError, orgData, plan, planLoading, usage, usageLoading }) => {
  // Editing state
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [saveError, setSaveError] = useState('');
  const [editedData, setEditedData] = useState({
    razonSocial: '',
    rfc: '',
    codigoPostal: '',
    regimenFiscal: '',
    usoCFDI: '',
  });

  // Billing state
  const [subscribing, setSubscribing] = useState(false);
  const [portalLoading, setPortalLoading] = useState(false);
  const [billingError, setBillingError] = useState('');

  // Subscription status helpers
  const subscriptionStatus = orgData?.subscriptionStatus;
  const hasActiveSubscription = subscriptionStatus === 'active' || subscriptionStatus === 'trialing';
  const isPastDue = subscriptionStatus === 'past_due';
  const isCanceled = subscriptionStatus === 'canceled' || subscriptionStatus === 'unpaid';

  // Handle Stripe Checkout
  const handleSubscribe = async () => {
    if (!orgData?.id) return;

    setBillingError('');
    setSubscribing(true);
    try {
      const { checkoutUrl } = await createCheckoutSession(orgData.id);
      window.location.href = checkoutUrl;
    } catch (err) {
      setBillingError(err.message || 'Error al iniciar el proceso de suscripción');
      setSubscribing(false);
    }
  };

  // Handle Stripe Portal
  const handleOpenPortal = async () => {
    if (!orgData?.id) return;

    setBillingError('');
    setPortalLoading(true);
    try {
      const { portalUrl } = await createPortalSession(orgData.id);
      window.location.href = portalUrl;
    } catch (err) {
      setBillingError(err.message || 'Error al abrir el portal de pagos');
      setPortalLoading(false);
    }
  };

  const handleStartEdit = () => {
    setEditedData({
      razonSocial: orgData?.razonSocial || '',
      rfc: orgData?.rfc || '',
      codigoPostal: orgData?.codigoPostal || '',
      regimenFiscal: orgData?.regimenFiscal || '',
      usoCFDI: orgData?.usoCFDI || '',
    });
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setSaveError('');
    setEditedData({
      razonSocial: '',
      rfc: '',
      codigoPostal: '',
      regimenFiscal: '',
      usoCFDI: '',
    });
  };

  const handleSave = async () => {
    if (!orgData?.id) return;

    // Validate código postal
    const cp = editedData.codigoPostal.trim();
    if (cp && cp.length !== 5) {
      setSaveError('El código postal debe tener exactamente 5 dígitos.');
      return;
    }

    setSaveError('');
    setIsSaving(true);
    try {
      await updateDoc(doc(db, "orgs", orgData.id), {
        razonSocial: editedData.razonSocial.trim().toUpperCase(),
        rfc: editedData.rfc.trim().toUpperCase(),
        codigoPostal: cp,
        regimenFiscal: editedData.regimenFiscal,
        usoCFDI: editedData.usoCFDI,
      });
      setIsEditing(false);
    } catch (error) {
      console.error("Error updating org billing data:", error);
      setSaveError('Error al guardar los datos.');
    } finally {
      setIsSaving(false);
    }
  };

  // Helper to get label from option value
  const getRegimenFiscalLabel = (value) => {
    const option = REGIMEN_FISCAL_OPTIONS.find(o => o.value === value);
    return option?.label || value;
  };

  const getUsoCFDILabel = (value) => {
    const option = USO_CFDI_OPTIONS.find(o => o.value === value);
    return option?.label || value;
  };

  const handleInputChange = (field, value) => {
    // Clear error when user edits código postal
    if (field === 'codigoPostal' && saveError) {
      setSaveError('');
    }
    setEditedData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Calculate plan costs
  const isMonthly = plan?.recurrence === 'monthly';
  const allowedUsers = plan?.allowed_users || 0;
  const extraUsers = plan?.extra_users || 0;
  const baseCost = plan?.cost || 0;
  const extraUserCost = plan?.extra_user_cost || 0;

  // For yearly, multiply by 10
  const displayBaseCost = isMonthly ? baseCost : baseCost * 10;
  const displayExtraUserCost = isMonthly ? extraUserCost : extraUserCost * 10;
  const totalExtraUsersCost = displayExtraUserCost * extraUsers;
  const subtotal = displayBaseCost + totalExtraUsersCost;
  const ivaAmount = plan?.iva_included ? 0 : subtotal * 0.16;
  const total = subtotal + ivaAmount;

  return (
    <div className="space-y-6">
      {/* Plan y Suscripción */}
      {planLoading ? (
        <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-6">
          <Text className="text-azul-surelia/70">Cargando información del plan...</Text>
        </div>
      ) : plan ? (
        <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <Heading level={2} className="text-xl font-bold text-azul-surelia">
                Plan {plan.name}
              </Heading>
              <Text className="text-azul-surelia/70">
                {isMonthly ? 'Suscripción mensual' : 'Plan anual'}
              </Text>
            </div>
            <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-azul-surelia/10 text-azul-surelia">
              {isMonthly ? 'Mensual' : 'Anual'}
            </span>
          </div>

          {/* Plan Details Grid */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <div className="bg-azul-surelia/5 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <UsersIcon className="w-5 h-5 text-azul-surelia/70" />
                <Text className="text-sm font-medium text-azul-surelia/70">Usuarios</Text>
                {!plan.limit_enforced && (
                  <div className="relative group">
                    <InformationCircleIcon className="w-4 h-4 text-azul-surelia/50 cursor-help" />
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                      Si excedes el límite, se cobrará el diferencial
                    </div>
                  </div>
                )}
              </div>
              <Text className="text-xl font-bold text-azul-surelia">
                {allowedUsers + extraUsers}
              </Text>
              <Text className="text-xs text-azul-surelia/50">
                {allowedUsers} incluidos{extraUsers > 0 && ` + ${extraUsers} extra`}
              </Text>
              <Text className="text-xs text-azul-surelia/50">
                Extra: {formatCurrency(extraUserCost)}/mes
                {!isMonthly && ` (${formatCurrency(extraUserCost * 10)}/año)`}
              </Text>
            </div>
            <div className="bg-azul-surelia/5 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <DocumentTextIcon className="w-5 h-5 text-azul-surelia/70" />
                <Text className="text-sm font-medium text-azul-surelia/70">Páginas</Text>
                {!plan.limit_enforced && (
                  <div className="relative group">
                    <InformationCircleIcon className="w-4 h-4 text-azul-surelia/50 cursor-help" />
                    <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 bg-gray-900 text-white text-xs rounded-lg opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap z-10 pointer-events-none">
                      Si excedes el límite, se cobrará el diferencial
                    </div>
                  </div>
                )}
              </div>
              <Text className="text-xl font-bold text-azul-surelia">
                {plan.page_limit?.toLocaleString() || 0}
              </Text>
              <Text className="text-xs text-azul-surelia/50">
                Extra: {formatCurrency(plan.extra_page_price || 0)}/pág
              </Text>
            </div>
            <div className="bg-azul-surelia/5 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <CloudIcon className="w-5 h-5 text-azul-surelia/70" />
                <Text className="text-sm font-medium text-azul-surelia/70">Almacenamiento</Text>
              </div>
              <Text className="text-xl font-bold text-azul-surelia">
                {plan.storage || 0} GB
              </Text>
            </div>
            <div className="bg-azul-surelia/5 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <CalendarDaysIcon className="w-5 h-5 text-azul-surelia/70" />
                <Text className="text-sm font-medium text-azul-surelia/70">Período</Text>
              </div>
              <Text className="text-xl font-bold text-azul-surelia">
                {isMonthly ? '1 Mes' : '1 Año'}
              </Text>
            </div>
          </div>

          {/* Cost Breakdown */}
          <div className="border-t border-azul-surelia/10 pt-4 mb-6">
            <Heading level={3} className="text-lg font-semibold text-azul-surelia mb-3">
              Desglose de Costos {isMonthly ? '(Mensual)' : '(Anual)'}
            </Heading>
            <div className="space-y-2">
              {isMonthly ? (
                <>
                  <div className="flex justify-between">
                    <Text className="text-azul-surelia/70">Plan {plan.name} ({allowedUsers} usuarios)</Text>
                    <Text className="font-medium">{formatCurrency(baseCost)}</Text>
                  </div>
                  {extraUsers > 0 && (
                    <div className="flex justify-between">
                      <Text className="text-azul-surelia/70">
                        Usuarios adicionales ({extraUsers} × {formatCurrency(extraUserCost)})
                      </Text>
                      <Text className="font-medium">{formatCurrency(extraUserCost * extraUsers)}</Text>
                    </div>
                  )}
                </>
              ) : (
                <>
                  {/* Plan cost */}
                  <div className="flex justify-between">
                    <Text className="text-azul-surelia/70">
                      Plan {plan.name} ({formatCurrency(baseCost)}/mes × 12 meses)
                    </Text>
                    <Text className="font-medium">{formatCurrency(baseCost * 12)}</Text>
                  </div>
                  <div className="flex justify-between text-green-600">
                    <Text className="text-green-600">Descuento plan (2 meses)</Text>
                    <Text className="font-medium">-{formatCurrency(baseCost * 2)}</Text>
                  </div>

                  {/* Extra users cost */}
                  {extraUsers > 0 && (
                    <>
                      <div className="flex justify-between pt-2">
                        <Text className="text-azul-surelia/70">
                          {extraUsers} usuario{extraUsers > 1 ? 's' : ''} extra ({formatCurrency(extraUserCost)}/mes × 12 meses)
                        </Text>
                        <Text className="font-medium">{formatCurrency(extraUserCost * extraUsers * 12)}</Text>
                      </div>
                      <div className="flex justify-between text-green-600">
                        <Text className="text-green-600">Descuento usuarios (2 meses)</Text>
                        <Text className="font-medium">-{formatCurrency(extraUserCost * extraUsers * 2)}</Text>
                      </div>
                    </>
                  )}
                </>
              )}
              <div className="flex justify-between border-t border-azul-surelia/10 pt-2">
                <Text className="text-azul-surelia/70">Subtotal</Text>
                <Text className="font-medium">{formatCurrency(subtotal)}</Text>
              </div>
              {!plan.iva_included && (
                <div className="flex justify-between">
                  <Text className="text-azul-surelia/70">IVA (16%)</Text>
                  <Text className="font-medium">{formatCurrency(ivaAmount)}</Text>
                </div>
              )}
              <div className="flex justify-between border-t border-azul-surelia/10 pt-2">
                <Text className="font-semibold text-azul-surelia">Total {isMonthly ? 'mensual' : 'anual'}</Text>
                <Text className="font-bold text-azul-surelia text-lg">{formatCurrency(total)}</Text>
              </div>
              {!isMonthly && (
                <Text className="text-xs text-green-600 text-right">
                  Equivale a {formatCurrency(subtotal / 12)}/mes (ahorras {formatCurrency((baseCost + extraUserCost * extraUsers) * 2)})
                </Text>
              )}
              {plan.iva_included && (
                <Text className="text-xs text-azul-surelia/50 text-right">IVA incluido</Text>
              )}
            </div>
          </div>

          {/* Payment/Subscription Section - Different for Monthly vs Yearly */}
          {isMonthly ? (
            /* Monthly: Stripe Subscription UI */
            <div className="border-t border-azul-surelia/10 pt-6">
              <Heading level={3} className="text-lg font-semibold text-azul-surelia mb-3">
                {hasActiveSubscription ? 'Suscripción' : 'Método de Pago'}
              </Heading>

              {/* Billing Error */}
              {billingError && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg">
                  <Text className="text-sm text-red-700">{billingError}</Text>
                </div>
              )}

              {/* Past Due Warning */}
              {isPastDue && (
                <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
                  <div className="flex items-start gap-3">
                    <ExclamationTriangleIcon className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <Text className="text-sm font-medium text-red-800">
                        Pago pendiente
                      </Text>
                      <Text className="text-sm text-red-700 mt-1">
                        Tu último pago no pudo ser procesado. Por favor actualiza tu método de pago
                        para continuar usando el servicio.
                      </Text>
                      <Button
                        onClick={handleOpenPortal}
                        disabled={portalLoading}
                        className="mt-3"
                      >
                        {portalLoading ? 'Abriendo...' : 'Actualizar método de pago'}
                      </Button>
                    </div>
                  </div>
                </div>
              )}

              {/* No Subscription - Show subscribe button */}
              {!subscriptionStatus && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-azul-surelia/10 rounded-lg flex items-center justify-center">
                      <CreditCardIcon className="w-6 h-6 text-azul-surelia" />
                    </div>
                    <div>
                      <Text className="font-medium text-gray-900">Tarjeta de crédito o débito</Text>
                      <Text className="text-sm text-gray-500">Se cargará automáticamente cada mes</Text>
                    </div>
                  </div>
                  <Button
                    onClick={handleSubscribe}
                    disabled={subscribing}
                    className="w-full"
                  >
                    {subscribing ? 'Redirigiendo a checkout...' : 'Suscribirse'}
                  </Button>
                </div>
              )}

              {/* Active/Trialing Subscription - Show status */}
              {hasActiveSubscription && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <SubscriptionStatus
                    orgData={orgData}
                    usage={usage}
                    usageLoading={usageLoading}
                    plan={plan}
                  />
                </div>
              )}

              {/* Canceled Subscription - Show reactivate option */}
              {isCanceled && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-6">
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 bg-gray-100 rounded-lg flex items-center justify-center">
                      <CreditCardIcon className="w-6 h-6 text-gray-500" />
                    </div>
                    <div>
                      <Text className="font-medium text-gray-900">Suscripción cancelada</Text>
                      <Text className="text-sm text-gray-500">Tu suscripción ha sido cancelada</Text>
                    </div>
                  </div>
                  <Button
                    onClick={handleSubscribe}
                    disabled={subscribing}
                    className="w-full"
                  >
                    {subscribing ? 'Redirigiendo a checkout...' : 'Reactivar suscripción'}
                  </Button>
                </div>
              )}
            </div>
          ) : (
            /* Yearly: No Stripe, manual payment arrangement */
            <div className="border-t border-azul-surelia/10 pt-6">
              <Heading level={3} className="text-lg font-semibold text-azul-surelia mb-3">
                Pago Anual
              </Heading>
              <div className="bg-azul-surelia/5 border border-azul-surelia/15 rounded-lg p-6">
                <Text className="font-medium text-azul-surelia mb-2">
                  Plan con facturación anual
                </Text>
                <Text className="text-sm text-azul-surelia/70 mb-4">
                  Tu plan se paga anualmente. El pago se coordina directamente con nuestro equipo
                  de ventas mediante transferencia bancaria o depósito.
                </Text>
                <div className="bg-white border border-azul-surelia/15 rounded-lg p-3">
                  <div className="flex justify-between items-center">
                    <Text className="text-sm text-azul-surelia/70">Costo anual total:</Text>
                    <Text className="font-bold text-azul-surelia text-lg">{formatCurrency(total)}</Text>
                  </div>
                  <Text className="text-xs text-azul-surelia/50 mt-1">
                    Equivalente a {formatCurrency(total / 12)}/mes (2 meses gratis vs. mensual)
                  </Text>
                </div>
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* Datos de Facturación */}
      <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-6">
        <div className="flex items-center justify-between mb-4">
          <Heading level={2} className="text-xl font-bold text-azul-surelia">Datos de Facturación</Heading>
          {!isEditing ? (
            <button
              onClick={handleStartEdit}
              className="p-2 text-azul-surelia/50 hover:text-azul-surelia hover:bg-azul-surelia/5 rounded-lg transition-colors"
              title="Editar datos de facturación"
            >
              <PencilIcon className="w-5 h-5" />
            </button>
          ) : (
            <div className="flex items-center gap-2">
              <button
                onClick={handleSave}
                disabled={isSaving}
                className="p-2 text-green-600 hover:bg-green-50 rounded-lg transition-colors disabled:opacity-50"
                title="Guardar"
              >
                <CheckIcon className="w-5 h-5" />
              </button>
              <button
                onClick={handleCancelEdit}
                disabled={isSaving}
                className="p-2 text-gray-500 hover:bg-gray-50 rounded-lg transition-colors"
                title="Cancelar"
              >
                <XMarkIcon className="w-5 h-5" />
              </button>
            </div>
          )}
        </div>
        <Text className="text-azul-surelia/70 mb-6">
          Información para la emisión de facturas de {orgData?.nombre || 'la organización'}
        </Text>

        {isEditing ? (
          <div className="space-y-6">
            {saveError && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                <Text className="text-sm text-red-700">{saveError}</Text>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-azul-surelia/70 mb-1">
                Razón Social
              </label>
              <Input
                value={editedData.razonSocial}
                onChange={(e) => handleInputChange('razonSocial', e.target.value.toUpperCase())}
                placeholder="RAZÓN SOCIAL DE LA EMPRESA"
                disabled={isSaving}
                className="uppercase"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-azul-surelia/70 mb-1">
                RFC
              </label>
              <Input
                value={editedData.rfc}
                onChange={(e) => handleInputChange('rfc', e.target.value.toUpperCase())}
                placeholder="RFC de la empresa"
                disabled={isSaving}
                className="font-mono uppercase"
                maxLength={13}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-azul-surelia/70 mb-1">
                Código Postal
              </label>
              <Input
                value={editedData.codigoPostal}
                onChange={(e) => handleInputChange('codigoPostal', e.target.value.replace(/\D/g, ''))}
                placeholder="Código postal"
                disabled={isSaving}
                className="font-mono"
                maxLength={5}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-azul-surelia/70 mb-1">
                Régimen Fiscal
              </label>
              <Listbox
                value={editedData.regimenFiscal}
                onChange={(value) => handleInputChange('regimenFiscal', value)}
                disabled={isSaving}
              >
                <ListboxOption value="">
                  <ListboxLabel>Selecciona un régimen fiscal</ListboxLabel>
                </ListboxOption>
                {REGIMEN_FISCAL_OPTIONS.map((option) => (
                  <ListboxOption key={option.value} value={option.value}>
                    <ListboxLabel>{option.label}</ListboxLabel>
                  </ListboxOption>
                ))}
              </Listbox>
            </div>
            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-azul-surelia/70 mb-1">
                Uso CFDI
              </label>
              <Listbox
                value={editedData.usoCFDI}
                onChange={(value) => handleInputChange('usoCFDI', value)}
                disabled={isSaving}
              >
                <ListboxOption value="">
                  <ListboxLabel>Selecciona un uso de CFDI</ListboxLabel>
                </ListboxOption>
                {USO_CFDI_OPTIONS.map((option) => (
                  <ListboxOption key={option.value} value={option.value}>
                    <ListboxLabel>{option.label}</ListboxLabel>
                  </ListboxOption>
                ))}
              </Listbox>
            </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <Text className="text-sm font-medium text-azul-surelia/70 mb-1">Razón Social</Text>
              <Text className="text-lg">
                {orgData?.razonSocial || <span className="text-gray-400 italic">No especificado</span>}
              </Text>
            </div>
            <div>
              <Text className="text-sm font-medium text-azul-surelia/70 mb-1">RFC</Text>
              <Text className="text-lg font-mono">
                {orgData?.rfc || <span className="text-gray-400 italic">No especificado</span>}
              </Text>
            </div>
            <div>
              <Text className="text-sm font-medium text-azul-surelia/70 mb-1">Código Postal</Text>
              <Text className="text-lg font-mono">
                {orgData?.codigoPostal || <span className="text-gray-400 italic">No especificado</span>}
              </Text>
            </div>
            <div>
              <Text className="text-sm font-medium text-azul-surelia/70 mb-1">Régimen Fiscal</Text>
              <Text className="text-lg">
                {orgData?.regimenFiscal ? getRegimenFiscalLabel(orgData.regimenFiscal) : <span className="text-gray-400 italic">No especificado</span>}
              </Text>
            </div>
            <div className="md:col-span-2">
              <Text className="text-sm font-medium text-azul-surelia/70 mb-1">Uso CFDI</Text>
              <Text className="text-lg">
                {orgData?.usoCFDI ? getUsoCFDILabel(orgData.usoCFDI) : <span className="text-gray-400 italic">No especificado</span>}
              </Text>
            </div>
          </div>
        )}

        {!isEditing && (!orgData?.razonSocial || !orgData?.rfc || !orgData?.codigoPostal || !orgData?.regimenFiscal || !orgData?.usoCFDI) && (
          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <Text className="text-sm text-amber-800">
              Completa tus datos de facturación haciendo clic en el ícono de editar.
            </Text>
          </div>
        )}
      </div>

      {/* Historial de Pagos */}
      <div className="bg-white border border-azul-surelia/15 rounded-xl shadow-sm p-6">
        <Heading level={2} className="text-xl font-bold text-azul-surelia mb-4">Historial de Pagos</Heading>
        <Text className="text-azul-surelia/70 mb-6">
          Pagos realizados por {orgData?.nombre || 'la organización'}
        </Text>

        {pagosLoading ? (
          <Text>Cargando pagos...</Text>
        ) : pagosError ? (
          <Text className="text-red-600">{pagosError}</Text>
        ) : pagos.length === 0 ? (
          <div className="text-center py-8">
            <Text className="text-gray-500">
              No tiene pagos registrados.
            </Text>
          </div>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeader>Fecha de Pago</TableHeader>
                <TableHeader>Subtotal</TableHeader>
                <TableHeader>IVA (16%)</TableHeader>
                <TableHeader>Total</TableHeader>
                <TableHeader>Validéz</TableHeader>
                <TableHeader>Válido Hasta</TableHeader>
                <TableHeader>Estado</TableHeader>
                <TableHeader>Factura</TableHeader>
              </TableRow>
            </TableHead>
            <TableBody>
              {pagos.map((pago) => {
                const validUntil = calculateValidUntil(pago.date, pago.monthsValidity);
                const isValid = isPaymentValid(pago.date, pago.monthsValidity);
                const { subtotal, iva, total } = derivePagoAmounts(pago);
                const xmlPath = pago.factura?.xmlPath;
                const pdfPath = pago.factura?.pdfPath;

                return (
                  <TableRow key={pago.id}>
                    <TableCell>
                      <Text>{formatDate(pago.date)}</Text>
                    </TableCell>
                    <TableCell>
                      <Text>{formatCurrency(subtotal)}</Text>
                    </TableCell>
                    <TableCell>
                      <Text>{formatCurrency(iva)}</Text>
                    </TableCell>
                    <TableCell>
                      <Text className="font-medium">{formatCurrency(total)}</Text>
                    </TableCell>
                    <TableCell>
                      <Text>{pago.monthsValidity} {pago.monthsValidity === 1 ? 'mes' : 'meses'}</Text>
                    </TableCell>
                    <TableCell>
                      <Text>{validUntil ? formatDate(validUntil) : '-'}</Text>
                    </TableCell>
                    <TableCell>
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        isValid
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {isValid ? 'Vigente' : 'Expirado'}
                      </span>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        {xmlPath && (
                          <button
                            type="button"
                            onClick={() => downloadFacturaFromStorage(xmlPath, `factura-${formatDate(pago.date).replace(/\s+/g, '-')}.xml`)}
                            title="Descargar XML"
                            className="inline-flex items-center justify-center w-7 h-7 rounded border border-azul-surelia/20 text-azul-surelia hover:bg-azul-surelia/5"
                          >
                            <CodeBracketIcon className="w-4 h-4" />
                            <span className="sr-only">Descargar XML</span>
                          </button>
                        )}
                        {pdfPath && (
                          <button
                            type="button"
                            onClick={() => openFacturaFromStorage(pdfPath)}
                            title="Descargar PDF"
                            className="inline-flex items-center justify-center w-7 h-7 rounded border border-azul-surelia/20 text-azul-surelia hover:bg-azul-surelia/5"
                          >
                            <DocumentArrowDownIcon className="w-4 h-4" />
                            <span className="sr-only">Descargar PDF</span>
                          </button>
                        )}
                        {!xmlPath && !pdfPath && (
                          <Text className="text-xs text-azul-surelia/40">-</Text>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        )}
      </div>
    </div>
  );
};

export default ConfiguracionFacturacion;
